import type { ChangeReasons } from '@/Network/Types/Requests/ChangeReasonRequests'
import {
  HistoryDate,
  PatchStationsRequest,
  StationTypes,
  type BulkSectorSpecialtyRequest
} from '@/Network/Types/Requests/StationRequests'
import type {
  BulkSectorSpecialtyResponse,
  MoveStationsResponse,
  PatchStationsResponse,
  RowConsultingResponse,
  StationSectorRef,
  StationSpecialtyRef
} from '@/Network/Types/Responses/StationResponses'
import type { BulkItem } from '@/modules/company/Dashboard/lenses/bulkPreview'
import { reactive } from 'vue'
import * as yup from 'yup'
import Network from '@/Network/'
import { parseDateStringToISO } from '@/utils/date'
import { FilterStates as UseFilterStatus } from '@/components/Filters/useFilterStation'

type States = {
  schedule?: number | null
  healthUnit?: number | null
  company?: number | null
  doctor?: number | null
  responsible?: string
  date?: string
  startHour?: string
  endHour?: string
  stationType?: string
  inCash?: boolean
  advanceRequested?: boolean
  advancePaid?: boolean
  timezone?: string
  value?: number
  ourTeam?: boolean
  isLoading: boolean
  histories?: HistoryDate[]
  hasErrorDate: boolean
  modifiedValue?: boolean
  observations: {
    date?: string
    description: string
    userName?: string
  }[]
  infos: {
    companyId: number
    stationValue: number
    urlConviteDoctor: string
    doctorId?: number
  }
}

export type FilterStates = {
  company?: number | null
  healthUnits?: number[] | null
  schedules?: number[] | null
  doctors?: number[] | null
  startDate?: string | null
  endDate?: string | null
  crmStates?: string[] | null
  crms?: string[] | null
  valueMin?: number | null
  valueMax?: number | null
  withCheckin?: boolean | null
  withCheckout?: boolean | null
  stationType?: string | null
  inCash?: boolean
  advanceRequested?: boolean
  advancePaid?: boolean
  ourTeam?: boolean
  status?: string[] | null
}

export const UpdateStationSchema = yup.object({
  // ids: yup.array(yup.number().required()).required().min(1),
  doctor: yup.string().optional().nullable(),
  responsible: yup.string().optional().nullable(),
  date: yup.string().optional(),
  startHour: yup.string().optional(),
  endHour: yup.string().optional(),
  timezone: yup.string(),
  ourTeam: yup.boolean().optional(),
  inCash: yup.boolean().optional(),
  stationType: yup.mixed<StationTypes>().oneOf(['COBERTURA', 'FIXO']).optional(),
  value: yup.number().optional(),
  schedule: yup.number().optional(),
  advancePaid: yup.boolean().optional(),
  advanceRequested: yup.boolean().optional(),
  modifiedValue: yup.boolean().optional()
})

export const filterStates: FilterStates = reactive({
  company: undefined,
  healthUnits: undefined,
  schedules: undefined,
  doctors: undefined,
  startDate: undefined,
  endDate: undefined,
  crmStates: undefined,
  crms: undefined,
  valueMin: undefined,
  valueMax: undefined,
  withCheckin: undefined,
  withCheckout: undefined,
  inCash: undefined,
  advanceRequested: undefined,
  advancePaid: undefined,
  status: undefined,
  stationType: undefined
})

export const formStates: States = reactive({
  company: undefined,
  healthUnit: undefined,
  schedule: undefined,
  doctor: undefined,
  date: undefined,
  endHour: undefined,
  ourTeam: undefined,
  inCash: undefined,
  advancePaid: undefined,
  advanceRequested: undefined,
  responsible: undefined,
  startHour: undefined,
  stationType: undefined,
  timezone: undefined,
  value: undefined,
  histories: undefined,
  isLoading: false,
  hasErrorDate: false,
  modifiedValue: undefined,
  infos: {
    companyId: 0,
    stationValue: 0,
    urlConviteDoctor: '',
    doctorId: undefined
  },
  observations: []
})

export function clearFilters() {
  filterStates.company = undefined
  filterStates.healthUnits = undefined
  filterStates.schedules = undefined
  filterStates.doctors = undefined
  filterStates.startDate = undefined
  filterStates.endDate = undefined
  filterStates.crmStates = undefined
  filterStates.crms = undefined
  filterStates.valueMin = undefined
  filterStates.valueMax = undefined
  filterStates.withCheckin = undefined
  filterStates.withCheckout = undefined
  filterStates.inCash = undefined
  filterStates.advanceRequested = undefined
  filterStates.advancePaid = undefined
  filterStates.status = undefined
  filterStates.ourTeam = undefined
  filterStates.stationType = undefined
}

export function clear() {
  formStates.company = undefined
  formStates.healthUnit = undefined
  formStates.schedule = undefined
  formStates.doctor = undefined
  formStates.responsible = undefined
  formStates.date = undefined
  formStates.startHour = undefined
  formStates.endHour = undefined
  formStates.timezone = undefined
  formStates.stationType = undefined
  formStates.value = undefined
  formStates.ourTeam = undefined
  formStates.inCash = undefined
  formStates.advancePaid = undefined
  formStates.advanceRequested = undefined
  formStates.isLoading = false
  formStates.hasErrorDate = false
  formStates.histories = []
}

export function validateDate() {
  const allFieldsEmpty =
    !formStates.date && !formStates.startHour && !formStates.endHour && !formStates.timezone
  const allFieldsUpdate =
    formStates.date && formStates.startHour && formStates.endHour && formStates.timezone
  if (allFieldsEmpty || allFieldsUpdate) {
    return true
  }
  formStates.hasErrorDate = true
  return false
}

/** `excludeSchedule`: o mover vai pela rota própria (`PATCH /stations/bulk/move`). */
type BaseSubmitOptions = { excludeSchedule?: boolean }

const getPayloadRequest = ({ excludeSchedule = false }: BaseSubmitOptions = {}) => {
  const data = UpdateStationSchema.validateSync(formStates)
  const payload: PatchStationsRequest = {
    config: {
      date: data.date ? parseDateStringToISO(data.date) : undefined,
      endHour: data.endHour ? data.endHour : undefined,
      startHour: data.startHour ? data.startHour : undefined,
      timezone: data.timezone ? data.timezone : undefined
    },
    ourTeam: data.ourTeam,
    stationType: data.stationType,
    doctorId: data.doctor ? +data.doctor : undefined,
    // userSponsorId?
    value: data.value,
    schedule: excludeSchedule ? undefined : data.schedule,
    inCash: data.inCash,
    modifiedValue: data.modifiedValue ?? undefined,
    advancePaid: data.advancePaid,
    advanceRequested: data.advanceRequested
  }

  return payload
}

/** Campos do formulário que entram no `PATCH /stations/bulk` (empresa/unidade sem escala não movem). */
const BASE_FORM_FIELDS = [
  'date',
  'startHour',
  'endHour',
  'timezone',
  'ourTeam',
  'stationType',
  'doctor',
  'value',
  'schedule',
  'inCash',
  'modifiedValue',
  'advancePaid',
  'advanceRequested'
] as const satisfies readonly (keyof States)[]

/**
 * Algum campo base do formulário foi preenchido? Sem nenhum, o `PATCH /stations/bulk` não tem
 * o que gravar — o modal da Consulta (flag ON) roda só o lote de setor/especialidade.
 */
export function hasBaseFormValues({ excludeSchedule = false }: BaseSubmitOptions = {}): boolean {
  return BASE_FORM_FIELDS.some((field) => {
    if (excludeSchedule && field === 'schedule') return false
    const value = formStates[field]
    return value !== undefined && value !== null && value !== ''
  })
}

export async function executeSubmit(
  ids?: number[],
  options: BaseSubmitOptions = {}
): Promise<PatchStationsResponse> {
  if (!ids) return {}
  const payload = getPayloadRequest(options)
  return Network.station.patchStations(ids, payload)
}

/** Mover da Consulta: `PATCH /stations/bulk/move` com os ids do PLANTÃO (nunca os da alocação). */
export async function executeMoveSubmit(
  stationIds: number[],
  targetScheduleId: number
): Promise<MoveStationsResponse> {
  return Network.station.moveStations(stationIds, targetScheduleId)
}

/** E1: `changeReasons` vai no topo do corpo do `/bulk/all`; os filtros seguem na query. */
export async function executeAllRowsSubmit(
  filters?: UseFilterStatus,
  changeReasons?: ChangeReasons
): Promise<PatchStationsResponse> {
  const payload = getPayloadRequest()
  return Network.station.patchAllStations(
    changeReasons ? { ...payload, changeReasons } : payload,
    filters
  )
}

/** `PATCH /stations/bulk/sector-specialty` (grava; `dryRun` removido por segurança). */
export async function executeSectorSpecialtySubmit(
  request: BulkSectorSpecialtyRequest
): Promise<BulkSectorSpecialtyResponse> {
  const { ids, sector, specialty } = request
  return Network.station.bulkSectorSpecialty({ ids, sector, specialty })
}

/** Prévia do lote: mesmo endpoint com `dryRun: true` (DECISIONS A8) — não grava. */
export async function previewSectorSpecialtySubmit(
  request: BulkSectorSpecialtyRequest
): Promise<BulkSectorSpecialtyResponse> {
  return Network.station.bulkSectorSpecialty({ ...request, dryRun: true })
}

/**
 * Normalizadores da linha da Consulta (A9): o web prefere os campos planos
 * (`sectorId`/`sectorName`, `specialtyId`/`specialtySource`/`specialtyName`) e cai na
 * forma aninhada (`sector`/`specialty`) que o plano do BE também descreve.
 */
export function rowSector(row: RowConsultingResponse): StationSectorRef | null {
  if (row.sectorId) return { id: row.sectorId, name: row.sectorName ?? '' }
  return row.sector ?? null
}

export function rowSpecialty(row: RowConsultingResponse): StationSpecialtyRef | null {
  if (row.specialtyId && row.specialtySource)
    return { id: row.specialtyId, name: row.specialtyName ?? '', source: row.specialtySource }
  return row.specialty ?? null
}

/** Id do PLANTÃO da linha (o `id` da linha é o da alocação). `null` = BE ainda sem `stationId`. */
export const rowStationId = (row: RowConsultingResponse): number | null => row.stationId ?? null

/**
 * Linha da Consulta → item do lote. Sem `stationId` (BE antigo) devolve `null`: a linha fica
 * FORA do lote — nunca cai no `id` da linha, que é o da alocação.
 */
export function rowToBulkItem(
  row: RowConsultingResponse,
  companyId: number | null
): BulkItem | null {
  const stationId = rowStationId(row)
  if (stationId === null) return null
  return {
    stationId,
    healthUnitId: row.healthUnitId ?? null,
    companyId,
    sector: rowSector(row),
    specialty: rowSpecialty(row)
  }
}

export function handleUpdateChecks() {
  clear()
}
